<template>
  <div
      class="item-hierarchy d-flex flex-column flex-grow-1 justify-content-start align-items-start border-dashed rounded my-2"
      :class="{'flex-grow-1 ': parentItem}"
      :key="itemKey">
    <div v-if="item" @click="toggleChildren"
         class="d-flex position-relative item-tree-container flex-grow-1 align-self-stretch px-5 py-3 bg-hover-light-primary"
         :class="{'border-bottom bg-light-primary' : showChildren}">
      <div :id="'item_' + item.uuid" :data-parent="parentItem ? parentItem.uuid : null"
           class="justify-content-start d-flex align-items-center justify-content-between flex-grow-1 d-flex">
        <div class="flex-grow-1 pe-10">
          <i class="fad fa-angle-right me-2" v-if="!showChildren"></i>
          <i class="fad fa-angle-down me-2" v-else></i>
          {{ property ? property.name : item.name }}
          <span class="text-muted ms-2">({{ item.code }})</span>
        </div>
        <!-- Boutons "tout cocher/décocher" retirés : créaient une ambiguïté (semblait toggler l'entité elle-même alors qu'ils togglaient en masse les sous-objets).
             Conservés en commentaire au cas où on voudrait les restaurer comme raccourci dépliable.
        <div class="d-flex gap-2" @click.stop>
          <div v-if="avenantType.allowAdd && objectProperties.length > 0"
               class="btn btn-success btn-xs btn-icon" :class="{'grayscale': !allAddChecked}"
               @click="toggleAllAdd" v-b-tooltip.hover
               :title="allAddChecked ? 'Décocher tous les ajouts' : 'Cocher tous les ajouts'">
            <i class="fad fa-plus"></i>
          </div>
          <div v-if="avenantType.allowRemove && objectProperties.length > 0"
               class="btn btn-danger btn-xs btn-icon" :class="{'grayscale': !allRemoveChecked}"
               @click="toggleAllRemove" v-b-tooltip.hover
               :title="allRemoveChecked ? 'Décocher tous les retraits' : 'Cocher tous les retraits'">
            <i class="fad fa-minus"></i>
          </div>
          <div v-if="avenantType.allowEdit && editableProperties.length > 0"
               class="btn btn-primary btn-xs btn-icon" :class="{'grayscale': !allEditChecked}"
               @click="toggleAllEdit" v-b-tooltip.hover
               :title="allEditChecked ? 'Décocher toutes les éditions' : 'Cocher toutes les éditions'">
            <i class="fad fa-edit"></i>
          </div>
        </div>
        -->
        <div class="text-muted fs-8">
          <span v-if="objectProperties.length > 0">{{ objectProperties.length }} sous-objet(s)</span>
          <span v-if="objectProperties.length > 0 && editableProperties.length > 0"> · </span>
          <span v-if="editableProperties.length > 0">{{ editableProperties.length }} propriété(s)</span>
        </div>
      </div>
    </div>
    <template v-if="item && showChildren">
      <div class="d-flex flex-column justify-content-start align-self-stretch">
        <template v-for="(prop , index ) in item.properties">
          <div v-if="!prop.internal" class="cursor-pointer d-flex py-1 flex-grow-1 bg-hover-light-primary px-5">
            <div class="d-flex flex-grow-1 m-1">
              <div class="flex-grow-1">
                {{ prop.name }}
                <span class="text-muted ms-1">({{ prop.code }})</span>
              </div>
              <div class="ms-10">
                <template v-if="prop.type && prop.type.code && prop.type.code.includes('OBJECT')">
                  <div class="btn btn-success btn-sm btn-icon"
                       :class="{'grayscale' : !avenantType.allowedAddition.includes(getSubItemCode(prop))}"
                       @click="toggleProperty('allowedAddition', getSubItemCode(prop))"
                       v-if="avenantType.allowAdd"
                       :title="'Autoriser l\'ajout de ' + (getSubItemCode(prop) || prop.name)">
                    <i class="fad fa-plus"></i>
                  </div>
                  <div class="btn btn-danger ms-1 btn-sm btn-icon"
                       :class="{'grayscale' : !avenantType.allowedRemove.includes(getSubItemCode(prop))}"
                       @click="toggleProperty('allowedRemove', getSubItemCode(prop))"
                       v-if="avenantType.allowRemove"
                       :title="'Autoriser le retrait de ' + (getSubItemCode(prop) || prop.name)">
                    <i class="fad fa-minus"></i>
                  </div>
                </template>
                <template v-else-if="avenantType.allowEdit">
                  <div class="btn btn-primary btn-sm btn-icon"
                       :class="{'grayscale' : !avenantType.allowedEdition.includes(prop.code)}"
                       @click="toggleProperty('allowedEdition', prop.code)">
                    <i class="fad fa-edit"></i>
                  </div>
                </template>
              </div>
            </div>
          </div>
        </template>
      </div>
    </template>
  </div>
</template>

<script lang="ts">
import {Vue, Component, Prop} from 'vue-property-decorator';
import Item from "@/entity/Item";
import ItemType from "@/entity/ItemType";
import Property from "@/entity/Property";
import AvenantType from "@/entity/AvenantType";
import Branch from "@/entity/Branch";

@Component({})
export default class ItemHierarchy extends Vue {
  @Prop() item!: ItemType
  @Prop() branch!: Branch
  @Prop() avenantType!: AvenantType
  @Prop() itemTypes!: ItemType[]
  @Prop() parentItem?: ItemType
  @Prop() property?: Property
  @Prop() itemKey: any
  showChildren = false

  get objectProperties(): any[] {
    return (this.item?.properties || []).filter((p: any) =>
        p.type?.code?.includes('OBJECT') && this.getSubItemCode(p)
    )
  }

  get editableProperties(): any[] {
    return (this.item?.properties || []).filter((p: any) =>
        !p.type?.code?.includes('OBJECT') && !p.internal
    )
  }

  get allAddChecked(): boolean {
    return this.objectProperties.every(p => this.avenantType.allowedAddition.includes(this.getSubItemCode(p)))
  }

  get allRemoveChecked(): boolean {
    return this.objectProperties.every(p => this.avenantType.allowedRemove.includes(this.getSubItemCode(p)))
  }

  get allEditChecked(): boolean {
    return this.editableProperties.every((p: any) => this.avenantType.allowedEdition.includes(p.code))
  }

  toggleAllAdd() {
    const checked = this.allAddChecked
    this.objectProperties.forEach(p => {
      const code = this.getSubItemCode(p)
      const included = this.avenantType.allowedAddition.includes(code)
      if (checked && included) this.toggleProperty('allowedAddition', code)
      if (!checked && !included) this.toggleProperty('allowedAddition', code)
    })
  }

  toggleAllRemove() {
    const checked = this.allRemoveChecked
    this.objectProperties.forEach(p => {
      const code = this.getSubItemCode(p)
      const included = this.avenantType.allowedRemove.includes(code)
      if (checked && included) this.toggleProperty('allowedRemove', code)
      if (!checked && !included) this.toggleProperty('allowedRemove', code)
    })
  }

  toggleAllEdit() {
    const checked = this.allEditChecked
    this.editableProperties.forEach((p: any) => {
      const included = this.avenantType.allowedEdition.includes(p.code)
      if (checked && included) this.toggleProperty('allowedEdition', p.code)
      if (!checked && !included) this.toggleProperty('allowedEdition', p.code)
    })
  }

  getSubItemCode(prop: any): string {
    const target = this.itemTypes.find((it: any) => it.uuid === prop.value)
    return target?.code || ''
  }

  toggleProperty(property: any, code: any) {
    if (!code) return
    this.$emit('toggleProperty', {property, uuid: code})
  }

  toggleChildren() {
    this.showChildren = !this.showChildren
  }
}
</script>
